import { FC, useEffect, useMemo, useState } from 'react';
import { Drawer, Dropdown } from 'antd';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { KeyResultI } from '~src/store/okrs/keyResultStream/interfaces';
import SelectorHandler from '~src/modules/okrs/manageObjectives/shared/SelectorHandler';
import { translateContent } from '~src/services/translationServices';
import CustomChip from '~src/modules/surveys/components/schedules/scheduleItem/templates/sections/customInputs/CustomChip';
import SvgIcon from '~src/shared/SvgIcon';
import AddKeyResultForm from '~src/modules/okrs/manageObjectives/create/keyResults/AddKeyResultForm';
import { KeyResultAdd } from '~src/modules/okrs/manageObjectives/create/keyResults/CreateKeyResults';
import { ownerToSegment, segmentToOwner } from '~src/modules/okrs/manageObjectives/shared/utils';
import { deleteKeyResult, editKeyResult } from '~src/store/okrs/keyResults/actions';
import { ApiReduxResp } from '~src/services/apiMiddleware';
import SearchableActivityList from '~src/modules/okrs/shared/activities/SearchableActivityList';
import { DdMenu } from '~src/shared/menus/dropdownBtn/DropdownBtn';
import IconTextItem from '~src/shared/IconTextItem';
import { MenuItemDel } from '~src/shared/headers/itemDetalsHeader/itemDetailsHeader.sc';
import ActionConfirmDialog from '~src/shared/dialogs/actionConfirmDialog/ActionConfirmDialog';
import { ErrRespItem } from '~src/shared/errors/interfaces';
import { showApiError } from '~src/shared/api/notification';
import { ObjectiveItem, ObjectiveStatus } from '~src/store/okrs/interfaces';
import { SessionUserI } from '~src/interfaces/session.types';
import { SlideIn } from '~src/modules/okrs/manageObjectives/edit/editListItem.sc';
import { DetailsHeader, DetailsWrapper } from '~src/modules/okrs/manageObjectives/edit/shared.sc';
import { useNotification } from '~src/services/hooks/useNotification';

import {
  ButtonWrapper,
  ChipsWrapper,
  EditHeader,
  EditSection,
  IconWrapper,
  ProgressWrapper,
  Section,
  Title,
} from './keyResultDetails.sc';
import KeyResultProgress from './KeyResultProgress';
import KeyResultUpdateButton from './KeyResultUpdateButton';

interface DispatchProps {
  editKeyResult: (keyResult: KeyResultI) => ApiReduxResp<KeyResultI>;
  deleteKeyResult: (keyResultId: string) => ApiReduxResp;
}

interface OwnProps {
  close: () => void;
  keyResult: KeyResultI;
  onUpdateKeyResult: () => void;
  objective: ObjectiveItem;
  sessionUser: SessionUserI | null;
}

type Props = OwnProps & DispatchProps;

const KeyResultDetails: FC<Props> = props => {
  const [t] = useTranslation();

  const [reloadActivities, setReloadActivities] = useState(false);
  const { notificationsFromAPIErrors } = useNotification();

  const sessionUserCanEdit = useMemo(() => {
    const sessionUserId = props.sessionUser && props.sessionUser.uuid;
    if (!sessionUserId) {
      return false;
    }
    const creatorUuid = props.objective.creator.uuid;
    const keyresultOwnerUuids = (props.keyResult.owners || []).map(o => o.uuid);
    const objectiveOwnerUuids = (props.objective.owners || []).map(o => o.uuid);

    return [creatorUuid]
      .concat(keyresultOwnerUuids)
      .concat(objectiveOwnerUuids)
      .some(userId => userId === sessionUserId);
  }, [props.sessionUser, props.keyResult, props.objective]);

  useEffect(() => {
    setReloadActivities(!reloadActivities);
  }, [props.keyResult]);

  const ownersSegments = useMemo(
    () => (props.keyResult.owners ? props.keyResult.owners.map(ownerToSegment) : null),
    [props.keyResult],
  );

  const [showEdit, setShowEdit] = useState(false);

  const showEditForm = () => setShowEdit(true);
  const hideEditForm = () => setShowEdit(false);

  const [loadingEdit, setLoadingEdit] = useState(false);

  const editKeyResultTrigger = (keyResultVal: KeyResultAdd) => {
    setLoadingEdit(true);
    const editedKeyResult = {
      ...props.keyResult,
      id: keyResultVal.id,
      name: {
        en_US: keyResultVal.name,
      },
      min: +keyResultVal.startValue,
      max: +keyResultVal.targetValue,
      owners: keyResultVal.owners.map(segmentToOwner),
      weighting: keyResultVal.weighting,
    };

    return props.editKeyResult(editedKeyResult).then(rsp => {
      setLoadingEdit(false);
      if (rsp.error) {
        notificationsFromAPIErrors(rsp.error.response.data, false, 'x.okr.error');

        return;
      }
      hideEditForm();
      props.onUpdateKeyResult();
    });
  };

  const editHeader = useMemo(
    () => (
      <EditHeader>
        <div onClick={hideEditForm}>
          <SvgIcon name="iconArrow" />
        </div>
        <span data-translation="x.okr.edit.key-result.edit.header">
          {t('x.okr.edit.key-result.edit.header')}
        </span>
      </EditHeader>
    ),
    [hideEditForm],
  );

  const formValue = useMemo<KeyResultAdd>(
    () => ({
      id: props.keyResult.id,
      name: translateContent(props.keyResult.name),
      startValue: props.keyResult.min.toString(),
      targetValue: props.keyResult.max.toString(),
      owners: ownersSegments || [],
      weighting: props.keyResult.weighting_locked ? props.keyResult.weighting : null,
    }),
    [props.keyResult, ownersSegments],
  );

  const [showDelConfirm, setShowDelConfirm] = useState(false);

  const onDelClick = () => {
    setShowDelConfirm(true);
  };

  const hideDelModal = () => {
    setShowDelConfirm(false);
  };

  const [deleteLoading, setDeleteLoading] = useState(false);

  const deleteKeyResultFn = async () => {
    setDeleteLoading(true);
    try {
      const rsp = await props.deleteKeyResult(props.keyResult.id);
      setShowDelConfirm(false);
      if (!!rsp.error) {
        rsp.error.response.data.forEach((item: ErrRespItem) => {
          showApiError(item);
        });

        return;
      }
      props.close();
    } finally {
      setDeleteLoading(false);
    }
  };

  const menu = (
    <DdMenu>
      <MenuItemDel onClick={onDelClick}>
        <IconTextItem iconName="iconBin" labelKey={'x.key_result.delete'} label={t('x.key_result.delete')} />
      </MenuItemDel>
    </DdMenu>
  );

  const isEnabled = sessionUserCanEdit && props.objective.status !== ObjectiveStatus.CLOSED;

  return (
    <>
      <ActionConfirmDialog
        t={t}
        show={showDelConfirm}
        close
        onHide={hideDelModal}
        actionTitleKey={'x.key_result.delete.modal.title'}
        actionContentKey={null}
        actionCall={deleteKeyResultFn}
        showDeletion
        loadingAction={deleteLoading}
      />

      <Drawer width={(window.innerWidth - 1170) / 2 + 390} onClose={hideEditForm} visible={showEdit}>
        {showEdit && (
          <AddKeyResultForm
            loadingAdd={loadingEdit}
            header={editHeader}
            selectedValues={formValue}
            addKeyResult={editKeyResultTrigger}
            stayOnSubmit
            hide={hideEditForm}
          />
        )}
      </Drawer>
      <SlideIn>
        <DetailsWrapper>
          <DetailsHeader>
            <IconWrapper onClick={props.close}>
              <SvgIcon name="iconCross" />
            </IconWrapper>
            {isEnabled && (
              <EditSection>
                <IconWrapper onClick={showEditForm}>
                  <SvgIcon name="iconPen" />
                </IconWrapper>
                <Dropdown
                  overlay={menu}
                  trigger={['click']}
                  overlayClassName="lls-dropdown-menu"
                  placement="bottom"
                >
                  <IconWrapper>
                    <SvgIcon name="iconThreeDots" />
                  </IconWrapper>
                </Dropdown>
              </EditSection>
            )}
          </DetailsHeader>
          <SelectorHandler iconName="iconClipBoard">
            {() => <Title>{translateContent(props.keyResult.name)}</Title>}
          </SelectorHandler>
          <ProgressWrapper>
            <KeyResultProgress keyResult={props.keyResult} />
          </ProgressWrapper>
          {isEnabled && (
            <ButtonWrapper>
              <KeyResultUpdateButton
                keyResult={props.keyResult}
                onUpdateKeyResult={props.onUpdateKeyResult}
              />
            </ButtonWrapper>
          )}
          <Section>
            <SelectorHandler iconName="iconPersonTool">
              {() => (
                <ChipsWrapper>
                  {ownersSegments &&
                    ownersSegments.map(owner => <CustomChip locked value={owner} key={owner.id} />)}
                </ChipsWrapper>
              )}
            </SelectorHandler>
          </Section>
          <Section>
            <SearchableActivityList id={props.keyResult.id} type="key_result" />
          </Section>
        </DetailsWrapper>
      </SlideIn>
    </>
  );
};

const mapDispatchToProps = (dispatch: any): DispatchProps => ({
  editKeyResult: keyResult => dispatch(editKeyResult(keyResult)),
  deleteKeyResult: keyResultId => dispatch(deleteKeyResult(keyResultId)),
});

const KeyResultDetailsConnected = connect(null, mapDispatchToProps)(KeyResultDetails);

export default KeyResultDetailsConnected;
